import React, { useContext } from 'react';
import CourseMuiCard from '../../../components/cards/MuiCard/CourseMuiCard/CourseMuiCard';
import { getCourseMuiCardProps } from '../../../components/cards/MuiCard/CourseMuiCard/utils';
import { Tab } from 'react-bootstrap';
import { MyCoursesTabEnum } from './MyCoursesPage';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { SearchLoadingStatus } from '../../../reducers/search.selectors';
import Loader from '../Loader';
import EmptyResults from '../EmptyResults';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { hasNext } from '../../../utils/pagination';
import { Pagination } from '../../../types/search';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import { Box } from '@mui/material';
import { cardDimensionsMedia, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';

const MyCoursesPageTab: React.FC<{
  tab: MyCoursesTabEnum;
  items: MindItem<'course'>[];
  loadingState: SearchLoadingStatus;
  pagination: Pagination;
  loadMore: () => void;
}> = ({ tab, items, loadingState, pagination, loadMore }) => {
  const { t } = useTranslation();
  const { mainContentContainer } = useContext(MasterLayoutContext);
  const { isSmallScreen, isLargeScreen } = useScreenSize();

  const getEmptyResultsText = () => {
    switch (tab) {
      case MyCoursesTabEnum.ALL:
        return t('You have not yet started or completed any courses.');

      case MyCoursesTabEnum.PROGRESS:
        return t('You have not started a course at the moment.');

      case MyCoursesTabEnum.SUCCESS:
        return t('You have not yet started any courses.');

      default:
        return '';
    }
  };

  const emptyResultsText = getEmptyResultsText();

  return (
    <Tab.Pane eventKey={tab} data-testid={`my-courses-${tab}-tab`}>
      <Box
        sx={{
          maxWidth: cardDimensionsMedia.desktop.maxWidth,
          marginX: 'auto',
          paddingY: spaces.lg,
          paddingX: isLargeScreen ? 0 : spaces.md,
          minHeight: '100%'
        }}>
        {[SearchLoadingStatus.INITIAL, SearchLoadingStatus.NONE].includes(loadingState) ? (
          <Loader />
        ) : (
          <>
            {items.length > 0 ? (
              <InfiniteScroll
                loadMore={loadMore}
                hasMore={hasNext(pagination)}
                useWindow={false}
                getScrollParent={() => mainContentContainer}
              >
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isSmallScreen ? spaces.sm : spaces.md,
                }}>
                  {items.map((courseMindItem, index) => (
                    <div key={courseMindItem.id}>
                      <CourseMuiCard
                        {...getCourseMuiCardProps(courseMindItem)}
                        index={index}
                      />
                    </div>
                  ))}
                </Box>
              </InfiniteScroll>
            ) : (
              <div>
                <EmptyResults
                  primaryText={t('No courses available')}
                  secondaryText={emptyResultsText} 
                />
              </div>
            )}
            {loadingState === SearchLoadingStatus.PAGINATION && (
              <Loader />
            )}
          </>
        )}
      </Box>

    </Tab.Pane>
  );
};

export default MyCoursesPageTab;
